import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { ContractChangeStatusEnum, ContractStatusEnum } from 'gql/graphql';
import { formatDateToLocale } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import {
    getChangeCreatedAtFilter,
    getChangeSigningDateFilter,
    getContractChangeInternalNumberFilter,
    getContractChangeStatusFilter,
    getContractChangeSubjectFilter,
} from '../AgreementsQueries';
import {
    AgreementsStatusRenderer,
    AgreementsLink,
    DateRenderer,
    KebabCellRenderer,
} from '../AgreementsRegistryTable/CellRenderers';

import { statusItemFromId } from './useAgreementStatuses';

const makeNullItemsFirstAndSort = (arr: { value: string | null }[]) =>
    arr
        ?.slice()
        // @ts-ignore
        ?.sort((a, b) => -(+(a.value === null) - +(b.value === null)) || +(a.value > b.value) || -(a.value < b.value));

const deleteDateDuplicatesHelper = (dateArr: { value: string }[]) => {
    const uniqHashTable: { [key: string]: boolean } = {};

    const onlyUniqDatesArr = dateArr?.filter((date) => {
        const localeDate = formatDateToLocale(date.value);

        if (uniqHashTable[localeDate]) {
            return false;
        }
        uniqHashTable[localeDate] = true;

        return true;
    });

    return makeNullItemsFirstAndSort(onlyUniqDatesArr);
};

const MenuCellStyle = { padding: 0 };

export const useRegistryTableColumns = ({
    openConfirmationDialogHandler,
    openEditDialogHandler,
    handleUploadAgreement,
    currentContractId,
    changeFilterHandler,
    isDisabledByProjectStatus,
    statusContract,
}: {
    openConfirmationDialogHandler: ({ id, status }: { id: number; status: ContractChangeStatusEnum }) => void;
    openEditDialogHandler: (agreementId: number, status: ContractChangeStatusEnum) => void;
    handleUploadAgreement: (id: number, status: ContractChangeStatusEnum) => void;
    currentContractId: number;
    changeFilterHandler: (values: any) => void;
    isDisabledByProjectStatus: boolean;
    statusContract?: ContractStatusEnum;
}) => {
    const columnDefs: (ColDef | ColGroupDef)[] = useMemo(
        () => [
            {
                field: 'menu',
                resizable: false,
                headerName: '',
                maxWidth: 40,
                cellStyle: MenuCellStyle,
                cellRenderer: KebabCellRenderer(
                    openConfirmationDialogHandler,
                    openEditDialogHandler,
                    handleUploadAgreement,
                    isDisabledByProjectStatus,
                    statusContract,
                ),
            },
            {
                headerName: 'Предмет ДС',
                field: 'subject',
                flex: 2,
                minWidth: 280,
                resizable: true,
                cellRenderer: AgreementsLink,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: {
                        contractId: currentContractId,
                    },
                    changeFilterHandler,
                    dataAccessor: 'uerContractChangeSubjectFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Предмет ДС',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: getContractChangeSubjectFilter,
                    groupAndSortItemsHelper: makeNullItemsFirstAndSort,
                },
                sortable: true,
            },
            {
                headerName: 'Статус ДС',
                field: 'status',
                flex: 1,
                minWidth: 180,
                maxWidth: 200,
                sortable: true,
                cellRenderer: AgreementsStatusRenderer,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: {
                        contractId: currentContractId,
                    },
                    changeFilterHandler,
                    dataAccessor: 'uerContractChangeStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус ДС',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: ContractChangeStatusEnum }) =>
                                statusItemFromId[value].label,
                            width: 400,
                        },
                    ],
                    query: getContractChangeStatusFilter,
                    withSearch: false,
                },
            },
            {
                headerName: 'Дата создания',
                field: 'createdAt',
                sortable: true,
                flex: 1,
                maxWidth: 200,
                cellRenderer: DateRenderer,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: {
                        contractId: currentContractId,
                    },
                    changeFilterHandler,
                    dataAccessor: 'uerContractChangeCreatedAtFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Дата создания',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => DateRenderer({ value }),
                            width: 400,
                        },
                    ],
                    query: getChangeCreatedAtFilter,
                    groupAndSortItemsHelper: deleteDateDuplicatesHelper,
                    withSearch: false,
                },
            },
            {
                headerName: 'Номер',
                field: 'internalNumber',
                flex: 1,
                maxWidth: 200,
                sortable: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: {
                        contractId: currentContractId,
                    },
                    changeFilterHandler,
                    dataAccessor: 'uerContractChangeInternalNumberFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Номер',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: any) => value || '<Без номера>',
                            width: 400,
                        },
                    ],
                    query: getContractChangeInternalNumberFilter,
                    groupAndSortItemsHelper: makeNullItemsFirstAndSort,
                },
            },
            {
                headerName: 'Дата подписания',
                field: 'signingDate',
                flex: 1,
                maxWidth: 200,
                sortable: true,
                cellRenderer: DateRenderer,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: {
                        contractId: currentContractId,
                    },
                    changeFilterHandler,
                    dataAccessor: 'uerContractChangeSigningDateFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус ДС',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string | null }) =>
                                value ? DateRenderer({ value }) : '<Без даты>',
                            width: 400,
                        },
                    ],
                    query: getChangeSigningDateFilter,
                    groupAndSortItemsHelper: makeNullItemsFirstAndSort,
                    withSearch: false,
                },
            },
        ],
        [
            changeFilterHandler,
            currentContractId,
            handleUploadAgreement,
            isDisabledByProjectStatus,
            openConfirmationDialogHandler,
            openEditDialogHandler,
            statusContract,
        ],
    );

    return columnDefs;
};
